import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import SeverityBadge from '@/components/apex/SeverityBadge';
import { ShieldCheck, Calendar, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SharedBoard() {
  const { token } = useParams();
  const [snap, setSnap] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/board-snapshots/${token}`)
      .then((r) => setSnap(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? 'not_found' : 'error'));
  }, [token]);

  if (error === 'not_found') return <ShellMessage title="Snapshot not found" body="This link is invalid or has been revoked." />;
  if (error) return <ShellMessage title="Could not load snapshot" body="Please try again later." />;
  if (!snap) return <ShellMessage title="Loading snapshot…" body="Fetching governance proof from the evidence fabric." spinner />;

  const d = snap.data;
  const debt = d.governance_debt;

  return (
    <div className="min-h-screen bg-apex-canvas text-apex-ink">
      {/* Banner */}
      <div className="bg-apex-shell text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-apex-lens/20 border border-apex-lens flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-apex-lens" />
            </div>
            <div>
              <div className="text-sm font-bold tracking-tight">APEX Lens</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <Lock className="w-2.5 h-2.5" />
                Read-only board snapshot
              </div>
            </div>
          </div>
          <div className="text-[10px] text-slate-400 font-mono text-right">
            <div>Generated {new Date(snap.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</div>
            <div>Token · {snap.token.slice(0, 6)}…</div>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-testid="shared-board">
        {/* Title */}
        <div>
          <span className="text-[10px] uppercase tracking-[0.18em] text-apex-lens font-semibold">Quarter Snapshot</span>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">{d.company}</h1>
          <p className="text-sm text-apex-neutral mt-2 max-w-3xl leading-relaxed">{d.board_reports.summary}</p>
          {d.note && (
            <div className="mt-4 inline-block rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 max-w-xl">
              <span className="font-semibold">Note · </span>{d.note}
            </div>
          )}
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Metric label="Evidence Readiness" value={`${d.platform_status.evidence_readiness}%`} accent="text-apex-success" />
          <Metric label="Governance Debt" value={`$${(debt.total_usd / 1000).toLocaleString()}K`} sub={`↓ ${Math.abs(debt.trend_pct_qtr)}% QoQ`} />
          <Metric label="Active Investigations" value={d.platform_status.active_investigations} sub="1 critical" accent="text-apex-warning" />
        </div>

        {/* Trend chart */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-bold text-apex-ink mb-3">12-Month Governance Debt Trend</h3>
          <div className="h-64 w-full min-h-[256px]">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <AreaChart data={debt.trend_12m.map((m) => ({ ...m, value_k: m.value / 1000 }))}>
                <defs>
                  <linearGradient id="sharedDebt" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0D9488" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#0D9488" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} stroke="#E2E8F0" />
                <YAxis tick={{ fontSize: 11, fill: '#64748B' }} stroke="#E2E8F0" tickFormatter={(v) => `$${v}K`} />
                <Tooltip contentStyle={{ backgroundColor: '#0F172A', borderRadius: 8, border: '1px solid #1E293B', color: 'white' }} />
                <Area type="monotone" dataKey="value_k" stroke="#0D9488" strokeWidth={2} fill="url(#sharedDebt)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top exposures */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-bold text-apex-ink mb-3">Top Active Exposures</h3>
          <div className="space-y-3">
            {d.board_reports.exposures.map((e, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }} className="flex items-start gap-3">
                <SeverityBadge severity={e.severity} size="sm" icon={false} />
                <p className="text-sm text-apex-ink-2 leading-snug">{e.label}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Framework readiness */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {d.framework_coverage.map((f) => (
            <div key={f.name} className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold">{f.name}</div>
              <div className="text-2xl font-bold tabular text-apex-ink mt-1">{f.coverage_pct}%</div>
              <div className="text-[11px] text-apex-neutral">{f.gaps} gap{f.gaps !== 1 ? 's' : ''}</div>
            </div>
          ))}
        </div>

        {/* Active investigations */}
        <div>
          <h3 className="text-sm font-bold text-apex-ink mb-3">Active Investigations</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {d.active_investigations.map((inv) => (
              <div key={inv.id} className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono text-apex-neutral">{inv.id}</span>
                  <SeverityBadge severity={inv.severity} size="sm" />
                </div>
                <p className="text-sm font-semibold text-apex-ink leading-snug">{inv.title}</p>
                <p className="text-[11px] text-apex-neutral mt-2">{inv.days_open} days · {inv.team}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center text-xs text-slate-400 font-mono pt-8 border-t border-slate-200 flex items-center justify-center gap-2">
          <Calendar className="w-3 h-3" />
          This snapshot is a deterministic, read-only export. Numbers may have changed since {new Date(snap.created_at).toLocaleDateString()}.
        </div>
      </main>
    </div>
  );
}

function Metric({ label, value, sub, accent = '' }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold">{label}</div>
      <div className={`text-3xl font-bold tabular text-apex-ink mt-1 ${accent}`}>{value}</div>
      {sub && <div className="text-[11px] text-apex-neutral mt-1 font-mono">{sub}</div>}
    </div>
  );
}

function ShellMessage({ title, body, spinner }) {
  return (
    <div className="min-h-screen bg-apex-canvas flex items-center justify-center p-6">
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center max-w-sm w-full">
        {spinner ? (
          <div className="w-8 h-8 mx-auto mb-3 border-2 border-apex-lens border-t-transparent rounded-full animate-spin" />
        ) : (
          <Lock className="w-8 h-8 text-slate-300 mx-auto mb-3" strokeWidth={1.4} />
        )}
        <h2 className="text-lg font-semibold text-apex-ink">{title}</h2>
        <p className="text-sm text-apex-neutral mt-1">{body}</p>
      </div>
    </div>
  );
}
